/**
 * Changelog Page Component
 * Displays the project changelog with version history
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BuildIcon from '@mui/icons-material/Build';
import BugReportIcon from '@mui/icons-material/BugReport';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NewReleasesIcon from '@mui/icons-material/NewReleases';

// Changelog data - this could be fetched from an API or file in the future
const changelogData = [
  {
    version: '2026-05-31',
    sections: {
      added: [
        'Dataset freshness table on the /data-sources page — sortable per-dataset currency view (last seen, expected cadence, status)',
        'Monthly scrapers for parliamentarian portraits (aph/parliamentarian_photos) and AEC redistributions (aec/redistributions), with state-file persistence under /mnt/redcar-data/parlsearch/scraper-state/',
      ],
      changed: [
        'Production data layer (Elasticsearch + Postgres + backend + admin) migrated from rick to summer; frontend stays rick-pinned for routing reliability. Per-scraper state files now persisted for the freshness endpoint to read.',
      ],
    }
  },
  {
    version: '2026-05-29',
    sections: {
      added: [
        '#226 - First, Most and More records hub Explore-stats page — youngest/oldest at first election, longest-serving, most ministerial roles, etc.',
        '#219 - Parliamentary office holders Explore-stats page (Speaker, President, Chairman of Committees, Speaker\'s Panel, …) across the unified ROSTypeID=8 corpus',
        '#218 - Members in both chambers Explore-stats page — ranks the 56 parliamentarians who served in both the House and the Senate',
        '#216 - Outcomes ledger Explore-stats page (Floor time) — Agreed / Negatived / Postponed / Passed buckets per sitting week',
        '#215 - Bills on the floor Explore-stats page (Floor time) — minutes per bill, per portfolio and per status',
        '#214 - Sitting day length Explore-stats page — per-chamber duration histogram, day-of-week medians, adjournment-hour distribution',
        '#212 - QT time-of-day Explore-stats page — question_number × minutes-after-14:00 scatter, QT-start drift and pace',
        '#211 - Question and answer length Explore-stats page — Q vs A box-plot, filibuster index, per-portfolio percentiles',
        '#207 - Top askers and responders Explore-stats page — dual ranked tables plus an asker × responder co-occurrence heatmap',
      ],
    }
  },
  {
    version: '2026-05-28',
    sections: {
      added: [
        '#213 - "What did Parliament spend its time on?" flagship Floor-time Explore-stats page — per-day 100%-stacked share of business categories',
        '#209 - Interjection meter Explore-stats page — per-day mean/max interjections, chair-share rate, noisiest portfolios',
        '#206 - Portfolio heatmap Explore-stats page — responder portfolio × sitting-week heatmap',
        '#205 - QT in numbers flagship Question Time overview Explore-stats page',
        '#201 - Presidents of the Senate Explore-stats page (every President since Federation, ranked by cumulative tenure)',
        '#200 - Speakers of the House of Representatives Explore-stats page (every Speaker since Federation, ranked by cumulative tenure)',
        'House Questions (QT) browse + search + detail pages (web UI for the new house_questions index)',
        'HouseLive Question Time dataset (house_questions) — one ES doc per question, sourced via SSM from the official Dept HouseLive prod SQLite',
      ],
      changed: [
        'House Questions detail page splits Question and Answer into separate prominent sections, with the full speaker-turn transcript available expandable',
      ],
    }
  },
  {
    version: '2026-05-27',
    sections: {
      added: [
        '#195 - Date filter gains a Decade → Year → Month → Day hierarchy for long-running datasets',
        '#192 - Landing page now surfaces all live datasets plus the Statistics hub',
        '#179 - Parliament + year chips on Votes & Proceedings result rows',
        '#178 - Sort control (Newest / Oldest / Relevance) on Votes & Proceedings results',
        '#188 - parlinfo/proceedings scraper gains --fetch-content to backfill V&P / Journals full text from each day\'s PDF',
        'Daily freshness monitor is now chamber-aware (Senate datasets no longer false-alarm during estimates weeks)',
      ],
      fixed: [
        '#194 - Date filter "+N more" years text is now an interactive expand/collapse control (was a dead affordance)',
        '#193 - Boolean (yes/no) facets are now pinned to the top of the filter list, consistently across datasets',
        '#177 - Votes & Proceedings detail page now surfaces structured keywords and themes as chips',
        '#176 - Votes & Proceedings "Nth Parliament" chip now renders (was reading the non-existent parliament_number field)',
        '#175 - Votes & Proceedings "HVP No." chip now renders (was reading the non-existent hvp_number field)',
        '#174 - Votes & Proceedings date drill-down day chip now actually filters results',
        '#173 - Graceful "full text not available" fallback for content-less Votes & Proceedings detail pages',
        'parlinfo/proceedings + parlinfo/chamber_documents catch-up via date window (was missing fresh House documents due to ParlInfo ParliamentNumber facet lag)',
      ],
    }
  },
  {
    version: '2026-05-26',
    sections: {
      added: [
        'Live Minutes browse/search page + per-item duration detail view — chamber filter, sortable table, ranked per-business-item duration visualisation',
      ],
    }
  },
  {
    version: '2026-05-25',
    sections: {
      added: [
        'Live (draft) Minutes dataset (live_minutes) — captures the House of Representatives\' ephemeral draft minutes with per-item time + duration metadata, one ES doc per chamber per sitting day',
        'Live Minutes extended to all three chambers — Federation Chamber via the same APH HTML page, Senate via the parlwork "Dynamic Red" SignalR feed',
      ],
    }
  },
  {
    version: '2026-05-24',
    sections: {
      added: [
        '#159 - Dark mode — light/dark theme toggle in the header, persisted, with system-default support and a hardcoded-colour sweep across charts, maps and primary surfaces',
        '#92 - ParlView video integration Phase 2 — per-speech "Watch this moment" deep-links, backed by per-fragment timestamps in Hansard plus a 1,694-day ParlView mapping backfill',
      ],
      fixed: [
        '#155 - Modern Hansard gap after 2025-10-30 closed (64 chamber-days backfilled), and the daily cron hardened with a --catch-up mode so gaps cannot re-accumulate',
        '#156 - Removed the double scrollbar in the left navigation sidebar',
        '#157 - Hansard search Speaker filter now actually constrains results (was leaking across speakers via the analyzed-tokens match)',
        '#158 - Hansard Parliament facet/filter now shows the correct parliament number (date-derived on ingest; 105k docs corrected in place)',
        '#160 - Hansard detail page navigation fixes — TOC click now scrolls to the speech, top "bills list" anchors no longer 404',
        '#161 - Hansard detail TOC clicks now update the URL — shareable speech deep-links + working browser Back/Forward',
        '#162 - Hansard advanced-search Chamber + Speaker filters fixed via a presentation-layer authority map (no data reprocessing); PHID-grouped speaker typeahead',
        '#163 - ParlView "Watch this moment" deep-links no longer land ~9m49s too early (corrected seek base to media_eom − file_duration)',
        '#164 - Parliament Statistics page — "Parliament Statistics" + "Party Composition" sections now render real numbers across all parliaments',
        '#165 - Divisions backfilled 2006–2026 + --catch-up gap recovery; vote arrays now stored as real JSON (frontend blank-page regression fixed)',
        '#166 - Handbook reference data refresh (committees, shadowministries, ministries, ministryrecords — frozen since 2025-12-15)',
        '#167 - Bills historical backfill across all 48 parliaments (parlinfo → 19,745 new docs; parlwork stream now stamped with parliament_number)',
        '#168 - hansard_historic 1981–1997 hole filled via a dedicated old-DTD SGML parser (498k → 1.09M docs; continuous 1901→1999)',
        '#169 - tabled_documents pre-2022 backfill via ParlInfo (OTD upstream is recent-only)',
        '#170 - senate_journals 2009–2012 hole filled (System Id regex was silently dropping date-sequence-shaped ids)',
        '#171 - notice_papers 2009–2013 hole filled (+ --catch-up + cron fixed)',
        '#172 - house_qiw historical backfill via House Hansard ("questions in writing" debates)',
      ],
    }
  },
  {
    version: '2026-05-23',
    sections: {
      added: [
        '#126 - Ministerial statement statistics Explore-stats page (per-year, per-Parliament, top ministers, top portfolios)',
      ],
    }
  },
  {
    version: '2026-05-22',
    sections: {
      added: [
        '#151 - Maintained scrapers for the four remaining STALE chamber-document indices — tabled_documents (OTD), notice_papers, senate_order_of_business, house_daily_program',
        '#124 - Committee membership statistics Explore-stats page (longest single service + most committees, with ID-keyed links to parliamentarians and committees)',
      ],
      fixed: [
        '#152 - parliamentarians, recordsofservice and recordsofservicetype indices are now refreshed daily from the Handbook OData API (had been frozen since 2025-12-15)',
        '#135 - Division and Tabled-Document results now link associated entities to their internal ParlSearch detail pages by stable ID',
      ],
    }
  },
  {
    version: '2026-05-18',
    sections: {
      added: [
        '#144 - Sitting days backfilled before 2020 — authoritative House V&P (from 1982) + Senate Journals (from 1973) into parliament_calendar (4,449 → 9,873 docs); /sitting-patterns now scans all docs so it can never be silently truncated',
      ],
    }
  },
  {
    version: '2026-05-17',
    sections: {
      added: [
        '#82 - Social sharing buttons on all 10 detail pages — native share sheet on mobile, X / Facebook / LinkedIn / Teams / email / copy-link menu elsewhere',
        '#77 - XML/SGML download for source records — original raw APH Hansard XML served byte-exact where held; structured XML fallback otherwise',
        '#76 - Server-side PDF download for Hansard fragments (reportlab; clean A4, metadata header, page numbers)',
        '#40 - Legislation passage statistics Explore-stats page — days through each House, by Parliament',
        '#39 - Standing Orders side-by-side comparison page with a dependency-free word-level LCS diff (HoR↔HoR, Senate↔Senate, or cross-chamber)',
        '#33 - Senate Order of Business ("the Red") is now searchable; House Daily Program is now reachable from the nav',
        '#101 - User Feature Request submission form (guided "As a / I need / So that" form + My Requests page + admin email)',
        '#95 - Natural-language search (bounded rule-based slice) — translates a small set of common phrases to Lucene, with a "Search this instead" Alert',
        '#94 - Content feedback mechanism — public POST /api/feedback + reusable Report Issue button on detail pages',
        '#88 - Helpful tooltips across the interface (sidebar collections, Hansard query syntax, facet accordions)',
        '#87 - Enhanced sort options on Hansard search — Relevance / Date (newest|oldest) / Title A–Z|Z–A, persisted per collection',
        '#86 - Guided Search Wizard — 4 step-by-step templates that deep-link into existing search pages with pre-built queries',
        '#85 - "Did you mean?" spelling-correction suggestions on Hansard search (ES phrase suggester with a collate query so suggestions actually match docs)',
        '#136 - Bills are no longer a dead end — new /bills/:id detail page; sponsoring members link to /parliamentarians/:phid by PHID',
        '#134 - User-sortable table columns across all 22 Explore-stats tables plus the Parliaments / Committees / Ministries / Shadow Ministries lists (shared type-aware hook)',
      ],
      fixed: [
        '#138, #139, #140, #141, #142, #143 - Hansard search filters now route through dedicated index-aware backend params (chamber, party, speaker_name, electorate, debate_type, parliament, date_from, date_to) and serialise into the URL — previously every Advanced field/facet was concatenated into the Lucene q string and silently broke',
        '#30 - Hansard records showed the wrong Parliament number — now derived from an authoritative date→parliament table built from the parliaments index',
        '#105 - Explore electorate map and election-year timeline cached per (year, state) with a 24h TTL (~13× faster on cache hit)',
        '#90 - Resolved automated WCAG 2.1 AA violations on homepage, Hansard and Parliamentarians (image-alt on avatars, accessible names on facet checkboxes, sidebar aria-controls + list structure)',
        '#137 - Direct navigation to a "coming soon" dataset path or any unknown URL now renders a friendly page instead of a silent blank main area',
        '#133 - Explore › Statistics tables now hyperlink parliamentarians / parties / electorates to their ID-based detail pages',
        '#104 - Electorate detail pages no longer attribute the wrong "current member" to a same-named electorate (Fraser ACT vs Victoria)',
        '#100 - Terms of Service + Privacy Policy on Login/Register are now real links to /terms and /privacy pages',
        '#98 - "Forgot your password?" link on the Login page (self-service reset to come)',
        '#99 - UserMenu disabled Profile item now shows a "Coming Soon" caption',
        '#97 - ExportCSVButton accessibility — aria-busy, aria-label, aria-describedby, role on snackbars',
        '#96 - PageSizeSelector dropdown now has an accessible name',
        '#45 - Parliamentarians Advanced Search dropdowns now list all values across the whole index (server-side aggregations, no more 100-record window)',
        '#132 - Senate Journals / V&P / Notice Paper detail pages no longer crash to blank (TDZ ReferenceError in the production bundle)',
      ],
    }
  },
  {
    version: '2026-05-16',
    sections: {
      added: [
        '#131 - Explore › Statistics: Prior elected service in government — current Parliament',
        '#130 - Explore › Statistics: Leaders of the Opposition (by party + cumulative service)',
        '#129 - Explore › Statistics: Prime Ministers (by party + cumulative length of service)',
        '#128 - Explore › Statistics: Parliamentarians who changed parties (Gantt + party-changes-per-year)',
        '#127 - Explore › Statistics: Parliamentary party statistics (Gantt timeline 1901→present, first/last Parliament)',
        '#125 - Explore › Statistics: Division statistics (agreed/negatived by Parliament since the 42nd)',
        '#123 - Explore › Statistics: Committee statistics (types by Parliament, members by type, longest-running, name word cloud)',
        '#122 - Explore › Statistics: Parliamentarians who changed electorate',
        '#121 - Explore › Statistics: Aboriginal and/or Torres Strait Islander Parliamentarians',
        '#120 - Explore › Statistics: Party representation by state — current Parliament',
        '#119 - Explore › Statistics: Party representation in each parliament',
        '#118 - Explore › Statistics: Longest serving members (over 20 years)',
        '#117 - Explore › Statistics: List of all women in Parliament (Gantt timeline, party-coloured)',
        '#116 - Explore › Statistics: Periods of service — current Parliament (bubble chart)',
        '#115 - Explore › Statistics: Length of service groups and distribution — current Parliament',
        '#114 - Explore › Statistics: Previous occupations — current Parliament',
        '#113 - Explore › Statistics: Levels of qualifications — current Parliament',
        '#112 - Explore › Statistics: Fields of qualifications — current Parliament',
        '#111 - Explore › Statistics: Overseas born (Leaflet world map with country bubbles)',
        '#110 - Explore › Statistics: Age groups and distribution',
        '#109 - Explore › Statistics: Gender composition in each Parliament',
        '#107, #108 - Explore › Statistics: foundation (router, shared scaffold, Recharts wrappers, hub at /explore/stats, sidebar nav) + Sitting pattern statistics — first of ~24 recreated Handbook pages',
      ],
      fixed: [
        '#103 - Parliamentarians search autocomplete options now load (request honours the API\'s per_page<=100 cap instead of 422-ing)',
        '#102 - favicon.svg no longer returns 403 site-wide (nginx image normalises static-asset permissions)',
      ],
    }
  },
  {
    version: '2026-01-02',
    sections: {
      added: [
        {
          title: '#91 - Statistics Dashboard in Admin Interface',
          items: [
            'Analytics service with Elasticsearch storage for usage tracking',
            'Automatic event logging middleware for search, view, and export events',
            'Dashboard with charts: daily activity, hourly distribution, dataset usage',
            'Popular search terms tracking',
            'Bot vs human traffic detection',
            'Date range filtering with quick presets (7d, 30d, 90d, 1yr)',
            'CSV export of analytics data',
            '8 new API endpoints under /api/admin/analytics/*',
          ]
        },
        'Changelog page accessible from Help page',
        'Alpha stamp badge in header indicating alpha release status',
      ],
      changed: [
        'Removed Docs link from header navigation (page still accessible at /doco)',
        'Documentation page updated with User Features and Admin Interface sections',
      ],
    }
  },
  {
    version: '2026-01-01',
    sections: {
      added: [
        {
          title: '#78 - User Authentication System',
          items: [
            'User registration with email and password',
            'Login/logout functionality with JWT tokens',
            'Session management with refresh tokens',
            'AuthContext for frontend state management',
            'LoginPage and RegisterPage components',
            'UserMenu component with avatar dropdown',
          ]
        },
        {
          title: '#80 - Saved Searches',
          items: [
            'Save current search with custom name from any search page',
            'SavedSearchesPage to view, run, edit, and delete saved searches',
            'SaveSearchButton component on all search result pages',
          ]
        },
        {
          title: '#81 - User Preferences',
          items: [
            'SettingsPage with configurable preferences',
            'Results per page (10, 25, 50, 100)',
            'Default sort order and snippet length settings',
            'Theme preference (light, dark, system)',
          ]
        },
        {
          title: '#79 - Email Alerts',
          items: [
            'AlertsPage to create and manage email alerts',
            'Create alerts from any saved search',
            'Configurable frequency (immediate, daily, weekly)',
            'Toggle active/inactive status and view delivery history',
          ]
        },
      ],
      changed: [
        'UserMenu now includes links to Saved Searches, Email Alerts, and Settings',
        'App.js wrapped with PreferencesProvider for global preferences state',
      ],
    }
  },
  {
    version: '2025-12-29',
    sections: {
      added: [
        {
          title: '#75 - CSV Export for Search Results',
          items: [
            'Export button on all search results pages',
            'Support for exporting up to 10,000 records',
            'Progress indicator for large exports',
          ]
        },
        {
          title: '#89 - Results Per Page Selector',
          items: [
            'Dropdown selector near pagination (10, 25, 50, 100)',
            'Selection persists in URL query parameter',
          ]
        },
        {
          title: '#93 - Legacy ParlInfo Permalink Redirects',
          items: [
            'Map legacy parlinfo.aph.gov.au URLs to new ParlSearch URLs',
            '301 permanent redirects for SEO',
          ]
        },
      ],
    }
  },
  {
    version: '2025-12-21',
    sections: {
      added: [
        {
          title: '#83 - Copy Permalink Button',
          items: [
            '"Copy Link" button on all detail pages',
            'Visual feedback with toast notification',
            'Works on mobile devices',
          ]
        },
      ],
    }
  },
  {
    version: '2025-12-19',
    sections: {
      added: [
        'Hierarchical date filter component for document search pages',
        'Year > Month > Day drill-down filtering for all document types',
        'Console error checking in Selenium tests for better QA coverage',
        'Server-side search for Name autocomplete in Parliamentarians advanced search',
      ],
      fixed: [
        '#34 - Fix "View Electorate" link on Parliamentarian detail page',
        '#35 - Fix "Division Votes" link to search votes_aye and votes_no fields',
        '#36 - Fix "Moved/Sponsored Bills" link to search movers field',
        '#38 - Fix ministries ordinal suffix displaying "1stth" instead of "1st"',
        '#44 - Add ACT and NT to State dropdown in Parliamentarians advanced search',
        '#71 - Implement hierarchical date filter across all document search pages',
        '#73 - Fix Senate Divisions date filter passing year to wrong API parameter',
        '#74 - Fix Historic Hansard Table of Contents navigation',
      ],
      changed: [
        'HansardSearch now uses reusable DateHierarchyFilter component',
        'Updated ken-test agent to capture JavaScript console errors during Selenium testing',
      ],
    }
  },
  {
    version: '2025-12-18',
    sections: {
      added: [
        'Initial date hierarchy filter implementation for Senate QON and House QIW',
        'Backend support for date_year and date_month query parameters',
      ],
      fixed: [
        'Senate QON and House QIW date field queries (changed from date to asked_date)',
      ],
    }
  },
];

const sectionConfig = {
  added: { icon: <AddIcon />, color: '#2e7d32', label: 'Added' },
  changed: { icon: <BuildIcon />, color: '#1565c0', label: 'Changed' },
  fixed: { icon: <BugReportIcon />, color: '#ed6c02', label: 'Fixed' },
  removed: { icon: <DeleteIcon />, color: '#d32f2f', label: 'Removed' },
};

function ChangelogPage() {
  const navigate = useNavigate();

  const renderItem = (item, index) => {
    if (typeof item === 'string') {
      return (
        <ListItem key={index} sx={{ py: 0.5, pl: 4 }}>
          <ListItemText
            primary={item}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      );
    }

    // Item with title and sub-items
    return (
      <Box key={index} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, pl: 4, mb: 0.5 }}>
          {item.title}
        </Typography>
        <List dense sx={{ py: 0 }}>
          {item.items.map((subItem, subIndex) => (
            <ListItem key={subIndex} sx={{ py: 0, pl: 6 }}>
              <ListItemText
                primary={`- ${subItem}`}
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          size="small"
        >
          Back to Help
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <NewReleasesIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4">
          Changelog
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        All notable changes to ParlSearch are documented here.
      </Typography>

      {changelogData.map((release, releaseIndex) => (
        <Paper
          key={release.version}
          elevation={1}
          sx={{ p: 3, mb: 3, borderRadius: 3 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Chip
              label={release.version}
              color={releaseIndex === 0 ? 'primary' : 'default'}
              sx={{ fontWeight: 600 }}
            />
            {releaseIndex === 0 && (
              <Chip label="Latest" size="small" color="success" variant="outlined" />
            )}
          </Box>

          {Object.entries(release.sections).map(([sectionKey, items]) => {
            const config = sectionConfig[sectionKey];
            if (!config || !items || items.length === 0) return null;

            return (
              <Box key={sectionKey} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32, color: config.color }}>
                    {config.icon}
                  </ListItemIcon>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: config.color }}
                  >
                    {config.label}
                  </Typography>
                </Box>
                <List dense sx={{ py: 0 }}>
                  {items.map((item, index) => renderItem(item, index))}
                </List>
              </Box>
            );
          })}
        </Paper>
      ))}

      <Paper elevation={0} sx={{ p: 3, bgcolor: '#f5f5f5', borderRadius: 3 }}>
        <Typography variant="body2" color="text.secondary">
          This changelog follows the{' '}
          <a
            href="https://keepachangelog.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1976d2' }}
          >
            Keep a Changelog
          </a>{' '}
          format. For the complete changelog, see the{' '}
          <a
            href="https://github.com/anthony-oio/parlsearch"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1976d2' }}
          >
            GitHub repository
          </a>.
        </Typography>
      </Paper>
    </Box>
  );
}

export default ChangelogPage;
